import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Star, ArrowRight, MapPin } from "lucide-react";
import Placeholder from "@/components/ui/Placeholder";
import SEO from "@/components/seo/SEO";
import GoogleReviews from "@/components/GoogleReviews";
import { RESTAURANT_LD, ORGANIZATION_LD, WEBSITE_LD, FAQ_LD } from "@/lib/jsonld";
import { useContenido } from "@/context/ContenidoContext";

// Word-by-word reveal for hero
function RevealWords({ children, delay = 0, className = "" }) {
    const words = String(children).split(" ");
    return (
        <span className={`inline-block ${className}`}>
            {words.map((w, i) => (
                <span key={i} className="inline-block overflow-hidden align-baseline mr-[0.25em]">
                    <motion.span
                        initial={{ y: "110%" }}
                        animate={{ y: "0%" }}
                        transition={{
                            duration: 1.1,
                            ease: [0.16, 1, 0.3, 1],
                            delay: delay + i * 0.06,
                        }}
                        className="inline-block"
                    >
                        {w}
                    </motion.span>
                </span>
            ))}
        </span>
    );
}

export default function Home() {
    const { t } = useTranslation();
    const { config } = useContenido();
    const chuletonPrecio = config?.chuleton?.precio || "56€/kg";
    const chuletonDesc =
        config?.chuleton?.descripcion ||
        "Maduración propia. Pregunta por las sugerencias del día.";

    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
    const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    return (
        <>
            <SEO
                title="Edificio del siglo XV · Restaurante en Ocaña"
                description="Restaurante Museo Tenería · Plaza del Mercado 3, Ocaña. Edificio único del siglo XV. Cocina tradicional manchega, parrilla premiada. Nº1 TripAdvisor Ocaña con 4,7★."
                keywords="restaurante Ocaña, restaurante Toledo, comer en Ocaña, chuletón Ocaña, parrilla Ocaña, cocina manchega Toledo, museo Ocaña, edificio siglo XV, restaurante cerca Aranjuez, restaurante cerca Tarancón"
                path="/"
                breadcrumbs={[{ name: "Inicio", item: "/" }]}
                jsonLd={[RESTAURANT_LD, ORGANIZATION_LD, WEBSITE_LD, FAQ_LD]}
            />
            {/* ========== HERO ========== */}
            <section
                ref={heroRef}
                className="relative min-h-[100vh] flex flex-col justify-center overflow-hidden paper-grain"
                style={{
                    background:
                        "radial-gradient(ellipse 80% 60% at 50% 40%, rgba(122,31,35,0.35) 0%, var(--bg) 70%)",
                }}
            >
                {/* Faded background number */}
                <motion.div
                    style={{ y: heroY, opacity: heroOpacity }}
                    aria-hidden
                    className="absolute inset-0 flex items-center justify-center pointer-events-none"
                >
                    <span
                        className="font-display italic text-[28vw] leading-none select-none"
                        style={{ color: "rgba(212, 168, 81, 0.025)" }}
                    >
                        XV
                    </span>
                </motion.div>

                <motion.div
                    style={{ y: heroY, opacity: heroOpacity }}
                    className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full"
                >
                    {/* Top metadata strip */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2, duration: 1 }}
                        className="flex items-center justify-between mb-16 md:mb-24"
                    >
                        <div className="flex items-center gap-4 text-stone">
                            <span className="w-12 h-px bg-gold/40" />
                            <span className="font-editorial italic text-xs tracking-[0.3em] uppercase text-gold">
                                {t("home.hero.anno")}
                            </span>
                        </div>
                        <span className="hidden md:inline-block font-editorial italic text-xs tracking-[0.3em] uppercase text-stone">
                            {t("home.hero.city")}
                        </span>
                    </motion.div>

                    {/* MASSIVE TYPOGRAPHY */}
                    <h1 className="font-display leading-[0.95] tracking-tight text-ivory">
                        <span className="block text-[clamp(2.5rem,7vw,6.5rem)] font-light">
                            <RevealWords delay={0.4}>{t("home.hero.word1")}</RevealWords>
                        </span>
                        <span
                            className="block text-[clamp(3.5rem,10vw,8.5rem)] italic font-light text-gold"
                            style={{ marginTop: "-0.05em", marginLeft: "0.15em" }}
                        >
                            <RevealWords delay={0.55}>{t("home.hero.word2")}</RevealWords>
                        </span>
                        <span className="block text-[clamp(2.5rem,7vw,6.5rem)] font-light text-right">
                            <RevealWords delay={0.7}>{t("home.hero.word3")}</RevealWords>
                        </span>
                    </h1>

                    {/* Asymmetric subtitle */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-16 md:mt-24 grid md:grid-cols-12 gap-8 items-end"
                    >
                        <div className="md:col-span-5 md:col-start-2">
                            <p className="font-body text-base md:text-lg text-stone leading-relaxed max-w-md">
                                {t("home.hero.tagline")}
                            </p>
                        </div>
                        <div className="md:col-span-5 md:col-start-8 flex flex-col sm:flex-row items-start sm:items-end gap-4">
                            <Link
                                to="/reservas"
                                className="btn-solid-alt"
                                data-testid="hero-book-button"
                                data-cursor="hover"
                            >
                                {t("home.hero.ctaBook")}
                            </Link>
                            <Link
                                to="/gastronomia"
                                className="btn-ghost"
                                data-testid="hero-menu-button"
                                data-cursor="hover"
                            >
                                {t("home.hero.ctaMenu")}
                            </Link>
                        </div>
                    </motion.div>

                    {/* Award badge — social proof above the fold (compact on mobile) */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.7, duration: 1 }}
                        className="mt-10 md:mt-14 flex justify-center md:justify-start md:pl-[8.333%]"
                    >
                        <Link
                            to="/premios"
                            data-testid="hero-premios-badge"
                            data-cursor="hover"
                            className="group inline-flex items-center gap-2 sm:gap-3 border border-gold/40 px-3 py-2 sm:px-5 sm:py-3 hover:border-gold hover:bg-gold/5 transition-all duration-500 max-w-full"
                        >
                            <Star
                                size={14}
                                className="text-gold shrink-0"
                                fill="currentColor"
                                strokeWidth={0}
                            />
                            <span className="font-editorial italic text-[0.65rem] sm:text-sm tracking-[0.1em] sm:tracking-[0.15em] uppercase text-ivory leading-tight">
                                <span className="block sm:inline">{t("home.hero.badge6Years")} </span>
                                <span className="text-gold">{t("home.hero.badgeAward")}</span>
                            </span>
                            <span className="hidden md:inline-block font-editorial italic text-[0.65rem] tracking-[0.25em] uppercase text-gold/70 group-hover:text-gold transition-colors">
                                {t("home.hero.badgeSource")}
                            </span>
                            <ArrowRight
                                size={12}
                                className="text-gold/70 group-hover:text-gold group-hover:translate-x-1 transition-all shrink-0"
                            />
                        </Link>
                    </motion.div>
                </motion.div>

                {/* Bottom strip — hidden on small screens to avoid colliding
                    with the award badge and the WhatsApp floating button. */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.8, duration: 1.2 }}
                    className="hidden md:flex absolute bottom-8 left-0 right-0 z-10 px-6 md:px-12 items-center justify-between text-xs tracking-[0.3em] uppercase font-editorial italic text-stone"
                >
                    <span>Plaza del Mercado · 3</span>
                    <span className="flex items-center gap-2">
                        {t("home.hero.scroll")}
                        <span className="block w-12 h-px bg-gold/60" />
                    </span>
                </motion.div>
            </section>

            {/* ========== CREDENTIAL STRIP — slow auto-scroll ========== */}
            {/*
              Mobile-first responsive grid:
                - <sm  : 2 columns (compact, tight pad), full label visible (smaller font).
                - sm+  : 2 columns with more breathing room.
                - md+  : 4 columns, original full layout.
              Each cell uses min-w-0 + word-break so long words like
              "ARQUEOPRAXIS" never get clipped by the cell border.
            */}
            <section className="credential-strip py-10 md:py-12 overflow-hidden">
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                    className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-4 sm:gap-x-6 md:gap-10 items-start"
                >
                    <div className="text-center md:text-left border-l border-gold/30 pl-3 sm:pl-5 md:pl-6 min-w-0">
                        <p className="font-display italic text-4xl md:text-5xl text-ivory leading-none">
                            4,7
                        </p>
                        <div className="flex gap-0.5 mt-1 justify-center md:justify-start text-gold">
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} size={10} fill="currentColor" strokeWidth={0} />
                            ))}
                        </div>
                        <p className="font-editorial italic text-[0.65rem] md:text-[0.7rem] tracking-[0.15em] md:tracking-[0.2em] uppercase text-stone mt-2 leading-tight">
                            1.053 reseñas
                        </p>
                    </div>
                    <div className="text-center border-l border-gold/30 pl-3 sm:pl-5 md:pl-6 min-w-0">
                        <p className="font-display italic text-xl md:text-2xl text-gold leading-tight">
                            N.º 1
                        </p>
                        <p className="font-editorial italic text-[0.65rem] md:text-[0.7rem] tracking-[0.15em] md:tracking-[0.2em] uppercase text-stone mt-2 leading-tight">
                            TripAdvisor · Ocaña
                        </p>
                    </div>
                    <div className="text-center border-l border-gold/30 pl-3 sm:pl-5 md:pl-6 min-w-0">
                        <p className="font-display italic text-lg md:text-2xl text-gold leading-tight">
                            Restaurant Guru
                        </p>
                        <p className="font-editorial italic text-[0.65rem] md:text-[0.7rem] tracking-[0.15em] md:tracking-[0.2em] uppercase text-stone mt-2 leading-tight">
                            Mejor parrilla
                        </p>
                    </div>
                    <div className="text-center md:text-left border-l border-gold/30 pl-3 sm:pl-5 md:pl-6 min-w-0">
                        <p className="font-display italic text-base sm:text-lg md:text-2xl text-gold leading-tight break-words">
                            ARQUEOPRAXIS
                        </p>
                        <p className="font-editorial italic text-[0.65rem] md:text-[0.7rem] tracking-[0.15em] md:tracking-[0.2em] uppercase text-stone mt-2 leading-tight">
                            Rehabilitación ejemplar
                        </p>
                    </div>
                </motion.div>
            </section>

            {/* ========== HISTORY — asymmetric, with roman numeral ========== */}
            <section className="relative py-32 md:py-48 overflow-hidden">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-20 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                        className="md:col-span-5 md:col-start-1"
                    >
                        <div className="aspect-[3/4] overflow-hidden">
    <img src="/images/platos/artesonado.webp" alt="Artesonado original del siglo XV en la antigua tenería de Ocaña" className="w-full h-full object-cover" />
</div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 1.4, delay: 0.2 }}
                        className="md:col-span-6 md:col-start-7"
                    >
                        <p className="roman-numeral">— {t("home.museo.chapter")} —</p>
                        <h2 className="font-display text-4xl md:text-6xl text-ivory font-light leading-tight mt-6">
                            {t("home.museo.title1")}
                            <span className="block italic text-gold mt-1">
                                {t("home.museo.title2")}
                            </span>
                        </h2>
                        <div className="line-gold my-10" />
                        <p className="font-body text-lg text-stone leading-[1.8] max-w-xl drop-cap">
                            {t("home.museo.p1")}
                        </p>
                        <p className="font-body text-base text-stone/80 leading-relaxed mt-6 max-w-xl">
                            {t("home.museo.p2")}
                        </p>
                        <Link
                            to="/museo"
                            className="btn-ghost mt-10"
                            data-cursor="hover"
                        >
                            {t("home.museo.cta")} <ArrowRight size={14} />
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* ========== SPACES — PARCHMENT inverted section ========== */}
            <section className="parchment py-32 md:py-48">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                        className="grid md:grid-cols-12 gap-10 items-end mb-20"
                    >
                        <div className="md:col-span-7">
                            <p className="font-editorial italic text-sm tracking-[0.3em] uppercase text-oxblood">
                                — {t("home.spaces.chapter")} —
                            </p>
                            <h2 className="font-display text-4xl md:text-6xl text-ink font-light leading-tight mt-6">
                                {t("home.spaces.title1")}
                                <span className="block italic text-oxblood">{t("home.spaces.title2")}</span>
                            </h2>
                        </div>
                        <div className="md:col-span-4 md:col-start-9">
                            <p className="font-body text-ink-soft leading-relaxed">
                                {t("home.spaces.intro")}
                            </p>
                        </div>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-x-8 gap-y-16">
                        {t("home.spaces.items", { returnObjects: true }).map((s, i) => {
                            const meta = [
                                { p: "Salón Principal", o: "interior", n: "I" },
                                { p: "Patio empedrado", o: "leaves", n: "II" },
                                { p: "Salón Privado", o: "architecture", n: "III" },
                            ][i];
                            return (
                            <motion.div
                                key={s.t}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{
                                    duration: 1,
                                    delay: i * 0.15,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                className={`group cursor-none ${i === 1 ? "md:mt-16" : ""}`}
                                style={{ color: "var(--ink)" }}
                            >
<div className="overflow-hidden aspect-[3/4]">
    <img
        src={`/images/platos/${["salon-principal","terraza-barricas","fachada-nocturna"][i]}.webp`}
        alt={s.t}
        className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]"
        loading="lazy"
    />
</div>
                                <p className="font-editorial italic text-xs tracking-[0.3em] uppercase text-oxblood mt-6">
                                    N.º {meta.n}
                                </p>
                                <h3 className="font-display italic text-3xl text-ink mt-2">
                                    {s.t}
                                </h3>
                                <p className="font-body text-ink-soft mt-3 text-sm leading-relaxed">
                                    {s.d}
                                </p>
                            </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ========== CHULETÓN — Massive feature ========== */}
            <section className="relative py-32 md:py-48 overflow-hidden">
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 1.2 }}
                        className="md:col-span-5"
                    >
<div className="aspect-square overflow-hidden">
    <img src="/images/platos/chuleton-brasa.webp" alt="Chuletón a la brasa especialidad del Restaurante Museo Tenería Ocaña 56€ el kilo" className="w-full h-full object-cover" />
</div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 1.3, delay: 0.15 }}
                        className="md:col-span-6 md:col-start-7"
                    >
                        <p className="roman-numeral">— {t("home.chuleton.chapter")} —</p>
                        <div className="flex items-center gap-3 mt-6 text-gold">
                            <Star size={14} fill="currentColor" strokeWidth={0} />
                            <Star size={14} fill="currentColor" strokeWidth={0} />
                            <span className="font-editorial italic text-xs tracking-[0.2em] uppercase">
                                {t("home.chuleton.badge")}
                            </span>
                        </div>
                        <h2 className="font-display text-5xl md:text-7xl text-ivory font-light leading-[0.95] mt-4">
                            {t("home.chuleton.title1")}
                            <span className="block italic text-gold">{t("home.chuleton.title2")}</span>
                        </h2>
                        <div className="line-gold my-10" />
                        <p className="font-body text-lg text-stone leading-relaxed max-w-xl">
                            {chuletonDesc} {t("home.chuleton.tail")}
                        </p>
                        <div className="flex items-baseline gap-6 mt-12">
                            <p className="font-display italic text-6xl md:text-7xl text-gold leading-none">
                                {chuletonPrecio}
                            </p>
                        </div>
                        <Link
                            to="/gastronomia"
                            className="btn-ghost mt-12"
                            data-cursor="hover"
                        >
                            {t("home.chuleton.cta")}
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* ========== GOOGLE REVIEWS — real social proof ========== */}
            <GoogleReviews variant="full" limit={3} />

            {/* ========== EVENTS ========== */}
            <section className="py-32 md:py-48 border-t border-gold/10">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                        className="text-center mb-20"
                    >
                        <p className="roman-numeral">— {t("home.events.chapter")} —</p>
                        <h2 className="font-display text-4xl md:text-6xl text-ivory font-light mt-6">
                            {t("home.events.title1")}
                            <span className="block italic text-gold">{t("home.events.title2")}</span>
                        </h2>
                    </motion.div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-gold/15">
                        {t("home.events.items", { returnObjects: true }).map((label, i) => {
                            const n = ["I", "II", "III", "IV"][i];
                            return (
                            <motion.div
                                key={label}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.9, delay: i * 0.1 }}
                                className="bg-bg p-10 md:p-14 group hover:bg-bg-warm transition-colors duration-700"
                            >
                                <p className="font-editorial italic text-xs tracking-[0.3em] uppercase text-gold/70">
                                    N.º {n}
                                </p>
                                <h3 className="font-display italic text-4xl text-ivory mt-4 group-hover:text-gold transition-colors duration-500">
                                    {label}
                                </h3>
                                <p className="font-body text-sm text-stone leading-relaxed mt-6 mb-8">
                                    {t("home.events.body")}
                                </p>
                                <Link
                                    to="/eventos"
                                    className="font-editorial italic text-sm text-gold link-underline"
                                    data-cursor="hover"
                                >
                                    {t("home.events.cta")}
                                </Link>
                            </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ========== FINAL CTA ========== */}
            <section className="py-32 md:py-48 text-center relative overflow-hidden">
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.5 }}
                    className="max-w-3xl mx-auto px-6"
                >
                    <p className="roman-numeral">— {t("home.finalCta.kicker")} —</p>
                    <h2 className="font-display text-5xl md:text-7xl text-ivory font-light mt-6 leading-[0.95]">
                        {t("home.finalCta.title1")}
                        <span className="block italic text-gold">{t("home.finalCta.title2")}</span>
                    </h2>
                    <div className="line-gold mx-auto my-12" />
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/reservas" className="btn-solid-alt" data-cursor="hover">
                            {t("home.finalCta.ctaBook")}
                        </Link>
                        <a href="tel:+34925484633" className="btn-ghost" data-cursor="hover">
                            925 48 46 33
                        </a>
                    </div>
                    <p className="font-editorial italic text-stone mt-16 inline-flex items-center gap-3 text-sm">
                        <MapPin size={14} className="text-gold" />
                        {t("home.finalCta.address")}
                    </p>
                </motion.div>
            </section>
        </>
    );
}
